import React, { useState } from "react"
import {
  Alert,
  Box,
  Button,
  Collapse,
  Paper,
  Stack,
  Typography,
} from "@mui/material"
import { useErrorHandler } from "../../hooks/useErrorHandler"
import { useNamespacedTranslation } from "../../hooks/useNamespacedTranslation"

type ErrorBoundaryProps = {
  componentName?: string
  children: React.ReactNode
}

type ErrorBoundaryBaseProps = {
  children: React.ReactNode
  onCatch?: (error: Error, info: React.ErrorInfo) => void
  fallback: (error: Error, reset: () => void) => React.ReactNode
}

type ErrorBoundaryBaseState = {
  error: Error | null
}

class ErrorBoundaryBase extends React.Component<
  ErrorBoundaryBaseProps,
  ErrorBoundaryBaseState
> {
  public state: ErrorBoundaryBaseState = { error: null }

  static getDerivedStateFromError(error: Error): ErrorBoundaryBaseState {
    return { error }
  }

  componentDidCatch(error: Error, info: React.ErrorInfo): void {
    this.props.onCatch?.(error, info)
  }

  private reset = () => {
    this.setState({ error: null })
  }

  render(): React.ReactNode {
    if (this.state.error) {
      return this.props.fallback(this.state.error, this.reset)
    }
    return this.props.children
  }
}

const ErrorFallback: React.FC<{ error: Error; onReset: () => void }> = ({
  error,
  onReset,
}) => {
  const [showDetails, setShowDetails] = useState(false)
  // Must read the dedicated `error` namespace: the bare `title`/`subtitle` keys exist in `common`
  // as unrelated template strings ("Título"/"Subtítulo") that used to leak into this panel.
  const { t } = useNamespacedTranslation("error")

  return (
    <Paper sx={{ p: 2, borderRadius: 2, border: (theme) => `1px solid ${theme.palette.divider}` }}>
      <Stack spacing={2}>
        <Alert severity="error" variant="outlined">
          <Typography variant="h6">{t("title", { defaultValue: "Something went wrong" })}</Typography>
          <Typography variant="body2" color="text.secondary">
            {t("subtitle", { defaultValue: "We hit a snag. Try again or expand to see details." })}
          </Typography>
        </Alert>

        <Stack direction="row" spacing={1} justifyContent="flex-start">
          <Button variant="contained" color="primary" onClick={onReset}>
            {t("tryAgain", { defaultValue: "Try again" })}
          </Button>
          <Button variant="text" onClick={() => setShowDetails((prev) => !prev)}>
            {showDetails
              ? t("hideDetails", { defaultValue: "Hide details" })
              : t("showDetails", { defaultValue: "Show details" })}
          </Button>
        </Stack>

        <Collapse in={showDetails}>
          <Box
            component="pre"
            sx={{
              backgroundColor: (theme) => theme.palette.background.default,
              p: 1.5,
              borderRadius: 1,
              border: (theme) => `1px solid ${theme.palette.divider}`,
              fontSize: 12,
              overflowX: "auto",
            }}
          >
            {error.stack || error.message}
          </Box>
        </Collapse>
      </Stack>
    </Paper>
  )
}

export const ErrorBoundary: React.FC<ErrorBoundaryProps> = ({
  componentName = "App",
  children,
}) => {
  const { t } = useNamespacedTranslation("error")
  const { handleError } = useErrorHandler(componentName)

  return (
    <ErrorBoundaryBase
      onCatch={(error, info) =>
        handleError(error, {
          operation: "render",
          userMessage: t("subtitle", { defaultValue: "We hit a snag. Try again or expand to see details." }),
          context: { componentStack: info.componentStack },
        })
      }
      fallback={(error, reset) => <ErrorFallback error={error} onReset={reset} />}
    >
      {children}
    </ErrorBoundaryBase>
  )
}
